/**
 * Per-user read state — synced with server (UserProjectActivity).
 * Session baseline remains in sessionStorage for in-tab comparison only.
 */

import {
  getCachedProjectAck,
  getCachedTaskSeenAt,
  setCachedProjectAck,
  setCachedTaskSeenAt,
  syncAcknowledgeProject,
  syncTaskSeen,
} from './activityStateStore';

const EPOCH_SENTINEL = 'epoch';

function visitBaselineKey(userId: string, projectId: string): string {
  return `pm:visitBaseline:${userId}:${projectId}`;
}

const endVisitTimers = new Map<string, ReturnType<typeof setTimeout>>();

function parseStoredDate(raw: string | null): Date | null {
  if (!raw || raw === EPOCH_SENTINEL) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function endVisitTimerKey(userId: string, projectId: string): string {
  return `${userId}:${projectId}`;
}

/** When the user last acknowledged all activity for this project (server-synced). */
export function getLastProjectVisit(userId: string | undefined, projectId: string): Date | null {
  if (!userId || !projectId) return null;
  return getCachedProjectAck(projectId);
}

export function setLastProjectVisit(
  userId: string | undefined,
  projectId: string,
  at: Date = new Date(),
): void {
  if (!userId || !projectId) return;
  setCachedProjectAck(projectId, at);
}

export function beginProjectVisit(userId: string | undefined, projectId: string): Date | null {
  if (!userId || !projectId) return null;
  try {
    const key = visitBaselineKey(userId, projectId);
    const existing = sessionStorage.getItem(key);
    if (existing !== null) {
      return parseStoredDate(existing);
    }
    const baseline = getLastProjectVisit(userId, projectId);
    sessionStorage.setItem(key, baseline ? baseline.toISOString() : EPOCH_SENTINEL);
    return baseline;
  } catch {
    return getLastProjectVisit(userId, projectId);
  }
}

export function getActiveVisitBaseline(userId: string | undefined, projectId: string): Date | null {
  if (!userId || !projectId) return null;
  try {
    const raw = sessionStorage.getItem(visitBaselineKey(userId, projectId));
    if (raw !== null) return parseStoredDate(raw);
  } catch {
    /* ignore */
  }
  return getLastProjectVisit(userId, projectId);
}

export function setActiveVisitBaseline(
  userId: string | undefined,
  projectId: string,
  at: Date | null,
): void {
  if (!userId || !projectId) return;
  try {
    sessionStorage.setItem(
      visitBaselineKey(userId, projectId),
      at ? at.toISOString() : EPOCH_SENTINEL,
    );
  } catch {
    /* ignore */
  }
}

export function endProjectVisit(userId: string | undefined, projectId: string): void {
  if (!userId || !projectId) return;
  try {
    sessionStorage.removeItem(visitBaselineKey(userId, projectId));
  } catch {
    /* ignore */
  }
}

export function scheduleEndProjectVisit(userId: string | undefined, projectId: string): void {
  if (!userId || !projectId) return;
  const timerKey = endVisitTimerKey(userId, projectId);
  const existing = endVisitTimers.get(timerKey);
  if (existing) clearTimeout(existing);
  endVisitTimers.set(
    timerKey,
    setTimeout(() => {
      endProjectVisit(userId, projectId);
      endVisitTimers.delete(timerKey);
    }, 150),
  );
}

export function cancelScheduledEndProjectVisit(userId: string | undefined, projectId: string): void {
  if (!userId || !projectId) return;
  const timerKey = endVisitTimerKey(userId, projectId);
  const existing = endVisitTimers.get(timerKey);
  if (existing) {
    clearTimeout(existing);
    endVisitTimers.delete(timerKey);
  }
}

export function getTaskSeenAt(userId: string | undefined, taskId: string): Date | null {
  if (!userId || !taskId) return null;
  return getCachedTaskSeenAt(taskId);
}

export function setTaskSeen(userId: string | undefined, taskId: string, at: Date = new Date()): void {
  if (!userId || !taskId) return;
  setCachedTaskSeenAt(taskId, at);
}

export async function acknowledgeProjectOnServer(projectId: string): Promise<void> {
  await syncAcknowledgeProject(projectId);
}

export async function markTaskSeenOnServer(taskId: string): Promise<{
  projectId: string;
  hasUnreadActivity: boolean;
}> {
  const result = await syncTaskSeen(taskId);
  return { projectId: result.projectId, hasUnreadActivity: result.hasUnreadActivity };
}
